import classNames from 'classnames';
import type { PropsWithChildren } from 'react';
import { useId } from 'react';

import { css } from '@sorare/css';

import { proxyUrl } from '#src/atoms/ui/ResponsiveImg/index.tsx';
import type { Color } from '#src/style/types.ts';

const root = css`
  position: relative;
  isolation: isolate;
  /* content-visibility: auto collapses off-screen cards to 0x0, which breaks
     horizontally-scrolled rows where a card peeks in. It also clips
     descendants to the card box, like contain: content. Opt out when eager
     or allowOverflow. */
  content-visibility: auto;
  contain: content;
  will-change: scroll-position;

  &[data-eager] {
    content-visibility: visible;
  }
  &[data-allow-overflow] {
    content-visibility: visible;
    contain: layout style;
  }

  &:after {
    content: var(--with-glow-content);
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
    pointer-events: none;
    mask-image: var(--mask-image-src);
    mask-repeat: no-repeat;
    mask-size: cover;
    background: radial-gradient(
      154.46% 96.82% at 50% 3.18%,
      transparent 75%,
      var(--glow-color) 100%
    );
  }
`;

type Props = PropsWithChildren<{
  active: boolean;
  color: Color;
  withGlow?: {
    src: string;
    color: Color;
  };
  className?: string;
  strokeWidth?: number;
  /** Render even when off-screen (disables content-visibility: auto). */
  eager?: boolean;
  /** Let children paint outside the card box (disables paint containment). */
  allowOverflow?: boolean;
}>;

export const StrokedCard = ({
  active,
  children,
  withGlow,
  color,
  className,
  strokeWidth = 2,
  eager,
  allowOverflow,
}: Props) => {
  const outlineId = `outline-${useId()}`;
  const { src, color: glowColor } = withGlow || {};

  return (
    <div
      data-eager={eager || undefined}
      data-allow-overflow={allowOverflow || undefined}
      style={
        {
          '--stroke-color': color,

          '--mask-image-src':
            active && src
              ? `url(${proxyUrl(src, { cropWidth: 160 })})`
              : 'none',

          '--with-glow-content': active && src ? '""' : 'none',
          '--glow-color': glowColor || 'transparent',
          filter: active ? `url(#${outlineId})` : undefined,
        } as React.CSSProperties
      }
      className={classNames(root, className, {
        active,
        withGlow: !!withGlow,
      })}
    >
      {!!active && (
        <svg className="absolute" width="0" height="0">
          <filter
            id={outlineId}
            {...(allowOverflow && {
              x: '-50%',
              y: '-50%',
              width: '200%',
              height: '200%',
            })}
          >
            <feMorphology
              operator="dilate"
              radius={strokeWidth}
              in="SourceAlpha"
              result="expanded"
            />
            <feFlood floodColor="var(--stroke-color)" result="strokeColor" />
            <feComposite
              in="strokeColor"
              in2="expanded"
              operator="in"
              result="stroke"
            />

            <feMerge>
              <feMergeNode in="stroke" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </svg>
      )}
      {children}
    </div>
  );
};
